import { Cpu } from './hardware/cpu-2a03/cpu';
import { Ppu2C02 } from './hardware/ppu-2c02';
import { Apu2A03 } from './hardware/apu-2a03';
import { Cartridge } from './interfaces/cartridge';
import { Controller, NullController } from './interfaces/controller';
import { MemoryBus } from './interfaces/memory-bus';

/**
 * The NES motherboard: owns the master clock relationship and routes the
 * CPU memory map. Integration-owned file — see AGENTS.md.
 *
 * CPU map:
 *   0x0000–0x1FFF  2KB internal RAM, mirrored
 *   0x2000–0x3FFF  PPU registers, mirrored every 8
 *   0x4000–0x4013  APU registers
 *   0x4014         OAM DMA
 *   0x4015         APU status
 *   0x4016         Controller 1 (write: strobe / read: serial data)
 *   0x4017         Controller 2 (read) / APU frame counter (write)
 *   0x4020–0xFFFF  Cartridge
 */
export class Nes implements MemoryBus {
    private ram: Uint8Array = new Uint8Array(0x0800);
    private cpu: Cpu;
    private ppu: Ppu2C02;
    private apu: Apu2A03;
    private cartridge: Cartridge;
    private controllers: Controller[] = [new NullController(), new NullController()];

    constructor(cartridge: Cartridge) {
        this.cartridge = cartridge;
        this.cpu = new Cpu(this);
        this.ppu = new Ppu2C02(this.cartridge, () => this.cpu.triggerNMI());
        this.apu = new Apu2A03(this);
        const apuAny = this.apu as unknown as { setIrqCallback?: (cb: (a: boolean) => void) => void };
        if (apuAny.setIrqCallback) {
            apuAny.setIrqCallback((asserted) => { if (asserted) this.cpu.triggerIRQ(); });
        }
    }

    setController(port: 0 | 1, controller: Controller): void {
        this.controllers[port] = controller;
    }

    read(address: number): number {
        address &= 0xFFFF;
        if (address < 0x2000) {
            return this.ram[address & 0x07FF];
        } else if (address < 0x4000) {
            return this.ppu.read(address & 0x07);
        } else if (address === 0x4016) {
            return this.controllers[0].read() & 0x01;
        } else if (address === 0x4017) {
            return this.controllers[1].read() & 0x01;
        } else if (address < 0x4020) {
            return this.apu.read(address);
        } else {
            return this.cartridge.cpuRead(address);
        }
    }

    write(address: number, value: number): void {
        address &= 0xFFFF;
        value &= 0xFF;
        if (address < 0x2000) {
            this.ram[address & 0x07FF] = value;
        } else if (address < 0x4000) {
            this.ppu.write(address & 0x07, value);
        } else if (address === 0x4014) {
            // OAM DMA: copy 256 bytes from CPU page $XX00–$XXFF into OAM.
            const startAddr = value << 8;
            for (let i = 0; i < 256; i++) {
                this.ppu.write(0x04, this.read(startAddr + i));
            }
            this.cpu.stall(513); // 514 on odd CPU cycles; close enough for games
        } else if (address === 0x4016) {
            this.controllers[0].writeStrobe(value);
            this.controllers[1].writeStrobe(value);
        } else if (address < 0x4020) {
            this.apu.write(address, value);
        } else {
            this.cartridge.cpuWrite(address, value);
        }
    }

    /**
     * Execute one CPU instruction and clock PPU/APU by the ACTUAL elapsed CPU
     * cycles — including OAM-DMA stalls and NMI/IRQ entry overhead, which
     * cpu.step()'s return value does not cover.
     */
    step(): number {
        const before = this.cpu.getCycles();
        const instructionCycles = this.cpu.step();
        if (instructionCycles === 0) {
            throw new Error(`CPU halted on unknown opcode at ${this.cpu.toString()}`);
        }
        const elapsed = this.cpu.getCycles() - before;
        this.ppu.step(elapsed);
        this.apu.step(elapsed);
        const cart = this.cartridge as { irqPending?: () => boolean };
        if (cart.irqPending && cart.irqPending()) {
            this.cpu.triggerIRQ();
        }
        return elapsed;
    }

    getCpu(): Cpu { return this.cpu; }
    getPpu(): Ppu2C02 { return this.ppu; }
    getApu(): Apu2A03 { return this.apu; }
    getCartridge(): Cartridge { return this.cartridge; }
}
